import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { requestAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isIT } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const response = await requestAPI.getById(id);
      setRequest(response.data);
    } catch (error) {
      toast.error('Talep yüklenirken hata oluştu');
      navigate('/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!resolution.trim()) {
      toast.error('Lütfen çözüm açıklaması girin');
      return;
    }

    setResolving(true);
    try {
      await requestAPI.update(id, {
        status: 'resolved',
        resolution: resolution.trim()
      });
      toast.success('Talep çözüldü olarak işaretlendi');
      setShowResolveModal(false);
      fetchRequest();
    } catch (error) {
      toast.error('İşlem başarısız');
    } finally {
      setResolving(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Açık' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'İşlemde' },
      pending: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Beklemede' },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Çözüldü' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Kapatıldı' }
    };
    const c = config[status] || config.open;
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Düşük' },
      medium: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Orta' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Yüksek' },
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Kritik' }
    };
    const c = config[priority] || config.medium;
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12 text-gray-500">
        Talep bulunamadı
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/requests')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Talep #{request.id}
            </h1>
            <p className="text-gray-500">{request.title}</p>
          </div>
        </div>
        {isIT() && request.status !== 'resolved' && request.status !== 'closed' && (
          <button
            onClick={() => setShowResolveModal(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            Çözüldü Olarak İşaretle
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Request Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Talep Detayları</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Başlık</label>
                <p className="font-medium">{request.title}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Açıklama</label>
                <p className="whitespace-pre-wrap">{request.description}</p>
              </div>
              <div className="flex gap-6">
                <div>
                  <label className="text-sm text-gray-500">Tür</label>
                  <p>
                    <span className={`px-2 py-1 text-xs rounded-full ${request.requestType?.category === 'issue' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                      {request.requestType?.name}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Öncelik</label>
                  <p>{getPriorityBadge(request.priority)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Durum</label>
                  <p>{getStatusBadge(request.status)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Failure Reason (if exists) */}
          {request.failureReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-red-800 mb-4">Arıza Sebebi</h2>
              <p className="whitespace-pre-wrap text-red-900">{request.failureReason}</p>
            </div>
          )}

          {/* Resolution (if resolved) */}
          {request.resolution && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-green-800 mb-4">Çözüm</h2>
              <p className="whitespace-pre-wrap text-green-900">{request.resolution}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-green-600">
                {request.resolvedBy && (
                  <p>
                    <span className="font-medium">Çözen:</span> {request.resolvedBy.fullName}
                  </p>
                )}
                {request.resolvedAt && (
                  <p>
                    <span className="font-medium">Tarih:</span> {format(new Date(request.resolvedAt), 'dd MMMM yyyy HH:mm', { locale: tr })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Related Device */}
          {request.device && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">İlgili Cihaz</h2>
              <Link
                to={`/devices/${request.device.id}`}
                className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50"
              >
                <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">💻</span>
                </div>
                <div>
                  <p className="font-medium">{request.device.name}</p>
                  <p className="text-sm text-gray-500">{request.device.brand} {request.device.model}</p>
                </div>
              </Link>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Requester Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Talep Eden</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-500">Ad Soyad</label>
                <p className="font-medium">{request.requester?.fullName}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">E-posta</label>
                <p>{request.requester?.email}</p>
              </div>
              {request.requester?.phone && (
                <div>
                  <label className="text-sm text-gray-500">Telefon</label>
                  <p>{request.requester?.phone}</p>
                </div>
              )}
              {request.requester?.department && (
                <div>
                  <label className="text-sm text-gray-500">Departman</label>
                  <p>{request.requester?.department}</p>
                </div>
              )}
              {request.requester?.location && (
                <div>
                  <label className="text-sm text-gray-500">Lokasyon</label>
                  <p>{request.requester?.location}</p>
                </div>
              )}
            </div>
          </div>

          {/* Assignment Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Atama Bilgisi</h2>
            {request.assignedTo ? (
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Atanan Kişi</label>
                  <p className="font-medium">{request.assignedTo.fullName}</p>
                </div>
                {request.assignedTo.email && (
                  <div>
                    <label className="text-sm text-gray-500">E-posta</label>
                    <p>{request.assignedTo.email}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Henüz atanmadı</p>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Zaman Çizelgesi</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Oluşturulma</span>
                <span>{format(new Date(request.createdAt), 'dd.MM.yyyy HH:mm')}</span>
              </div>
              {request.resolvedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Çözüldü</span>
                  <span>{format(new Date(request.resolvedAt), 'dd.MM.yyyy HH:mm')}</span>
                </div>
              )}
              {request.closedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Kapatıldı</span>
                  <span>{format(new Date(request.closedAt), 'dd.MM.yyyy HH:mm')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-4">Talebi Çözüldü Olarak İşaretle</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Çözüm Açıklaması *
              </label>
              <textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                placeholder="Yapılan işlemleri ve çözümü açıklayın..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResolveModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={resolving}
              >
                İptal
              </button>
              <button
                onClick={handleResolve}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                disabled={resolving}
              >
                {resolving ? 'Kaydediliyor...' : 'Çözüldü Olarak İşaretle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDetail;
